/**
 * CAC (Cloud Access Control) Allowed Domains
 *
 * This file contains all the allowed domains for various cloud services
 * that should not be added to the bypass list as they are already managed
 * by the CAC system.
 */

import {CACService, CACServices, CloudAppListTypes} from 'interfaces'
import {getCacConfiguredValues} from './CloudApp/cloudApp.utils'

export const CAC_ALLOWED_DOMAINS: CACServices = {
  box: {
    displayName: 'Box',
    allowedUrls: [
      '*.box.com',
      '*.box.net',
      '*.boxcdn.net',
      '*.boxcloud.com',
      'app.box.com',
      'upload.app.box.com',
    ],
    customDomains: [],
    isEnabled: false,
  },
  chatgpt: {
    displayName: 'ChatGPT',
    allowedUrls: [
      '*.browser-intake-datadoghq.com',
      '*.challenges.cloudflare.com',
      '*.chatgpt.com',
      '*.featureassets.org',
      '*.featuregates.org',
      '*.imgix.net',
      '*.intercom.io',
      '*.intercomcdn.com',
      '*.launchdarkly.com',
      '*.oaistatic.com',
      '*.oaiusercontent.com',
      '*.openai.com',
      '*.prodregistryv2.org',
      '*.sentry.io',
      '*.statsig.com',
      '*.statsigapi.net',
      '*.stripe.com',
      '*.workos.com',
      '*.workoscdn.com',
    ],
    customDomains: [],
    isEnabled: false,
  },
  claude: {
    displayName: 'Claude',
    allowedUrls: [
      '*.claude.ai',
      '*.support.anthropic.com',
      '*.api.anthropic.com',
      '*.console.anthropic.com',
      '*.statsig.anthropic.com',
    ],
    customDomains: [],
    isEnabled: false,
  },
  dropbox: {
    displayName: 'Dropbox',
    allowedUrls: [
      '*.addtodropbox.com',
      '*.app.hellosign.com',
      '*.cdn.arkoselabs.com',
      '*.db.tt',
      '*.dropbox-api.arkoselabs.com',
      '*.dropbox-dns.com',
      '*.dropbox.com',
      '*.dropbox.tech',
      '*.dropbox.zendesk.com',
      '*.dropboxapi.com',
      '*.dropboxbusiness.com',
      '*.dropboxcaptcha.com',
      '*.dropboxforum.com',
      '*.dropboxforums.com',
      '*.dropboxinsiders.com',
      '*.dropboxmail.com',
      '*.dropboxpartners.com',
      '*.dropboxstatic.com',
      '*.dropboxteam.com',
      '*.dropboxusercontent.com',
      '*.getdropbox.com',
      '*.links.dropbox.com',
      '*.paper-attachments.s3.amazonaws.com',
    ],
    customDomains: [],
    isEnabled: false,
  },
  github: {
    displayName: 'GitHub',
    allowedUrls: [
      '*.github.com',
      '*.githubcopilot.com',
      '*.githubusercontent.com',
      '*.githubassets.com',
    ],
    customDomains: [],
    isEnabled: false,
  },
  google: {
    displayName: 'Google',
    allowedUrls: [
      '*.accounts.youtube.com',
      '*.gmail.com',
      '*.google-analytics.com',
      '*.google.com',
      '*.googleapis.com',
      '*.googledrive.com',
      '*.googlegroups.com',
      '*.googleusercontent.com',
      '*.gstatic.com',
      '*.gvt1.com',
      '*.youtube-nocookie.com',
      '*.ytimg.com',
    ],
    customDomains: [],
    isEnabled: false,
  },
  o365: {
    displayName: 'Microsoft O365',
    allowedUrls: [
      '*.aadrm.com',
      '*.aka.ms',
      '*.akamaihd.net',
      '*.aspnetcdn.com',
      '*.azure.com',
      '*.azure.net',
      '*.azureedge.net',
      '*.azurerms.com',
      '*.azure-apim.net',
      '*.cloudapp.net',
      '*.cloud.microsoft',
      '*.cloudappsecurity.com',
      '*.cortana.ai',
      '*.edgesuite.net',
      '*.gfx.ms',
      '*.live.com',
      '*.live.net',
      '*.lync.com',
      '*.microsoft.com',
      '*.microsoft365.com',
      '*.microsoftazuread-sso.com',
      '*.microsoftonline-p.net',
      '*.microsoftonline-p.com',
      '*.microsoftonline.com',
      '*.microsoftstream.com',
      '*.microsoftusercontent.com',
      '*.msauth.net',
      '*.msauthimages.net',
      '*.msecnd.net',
      '*.msedge.net',
      '*.msft.net',
      '*.msftauth.net',
      '*.msftauthimages.net',
      '*.msftidentity.com',
      '*.msidentity.com',
      '*.mstea.ms',
      '*.msocdn.com',
      '*.mx.microsoft',
      '*.o365weve.com',
      '*.office.com',
      '*.office.net',
      '*.office365.com',
      '*.onedrive.com',
      '*.onenote.com',
      '*.onenote.net',
      '*.onestore.ms',
      '*.onmicrosoft.com',
      '*.optimizely.com',
      '*.outlook.com',
      '*.phonefactor.net',
      '*.powerapps.com',
      '*.powerautomate.com',
      '*.sfbassets.com',
      '*.sfx.ms',
      '*.sharepoint.com',
      '*.sharepointonline.com',
      '*.skype.com',
      '*.skypeassets.com',
      '*.skypeforbusiness.com',
      '*.staffhub.ms',
      '*.static.microsoft',
      '*.svc.ms',
      '*.tenor.com',
      '*.trafficmanager.net',
      '*.virtualearth.net',
      '*.visualstudio.com',
      '*.windows.com',
      '*.windows.net',
      '*.windowsazure.com',
    ],
    customDomains: [],
    isEnabled: false,
  },
  salesforce: {
    displayName: 'Salesforce',
    allowedUrls: ['*.force.com', '*.salesforce.com'],
    customDomains: [],
    isEnabled: false,
  },
  slack: {
    displayName: 'Slack',
    allowedUrls: [
      '*.chime.aws',
      '*.slack-edge.com',
      '*.slack-files.com',
      '*.slack-imgs.com',
      '*.slack.com',
      '*.slack.global.ssl.fastly.net',
      '*.slackb.com',
    ],
    customDomains: [],
    isEnabled: false,
  },
  webex: {
    displayName: 'Cisco WebEx',
    allowedUrls: [
      '*.accompany.com',
      '*.cisco.com',
      '*.ciscospark.com',
      '*.data.logentries.com',
      '*.googleapis.com',
      '*.sli.do',
      '*.slido.com',
      '*.vbrickrev.com',
      '*.walkme.com',
      '*.walkmeusercontent.com',
      '*.wbx2.com',
      '*.webex.com',
      '*.webexcontent.com',
    ],
    customDomains: [],
    isEnabled: false,
  },
}

const emptyCACService = (displayName: string): CACService => ({
  displayName,
  allowedUrls: [],
  customDomains: [],
  isEnabled: false,
})

/**
 * CAC allowed URL patterns that surface as a bypass error when they appear in the
 * bypass list. Schema matches {@link CAC_ALLOWED_DOMAINS}. Do not modify
 * {@link CAC_ALLOWED_DOMAINS} for this behavior.
 */
export const CAC_ALLOWED_DOMAINS_ERROR: CACServices = {
  box: emptyCACService('Box'),
  chatgpt: emptyCACService('ChatGPT'),
  claude: emptyCACService('Claude'),
  dropbox: emptyCACService('Dropbox'),
  github: {
    displayName: 'GitHub',
    allowedUrls: CAC_ALLOWED_DOMAINS.github.allowedUrls,
    customDomains: [],
    isEnabled: false,
  },
  google: emptyCACService('Google'),
  o365: emptyCACService('Microsoft O365'),
  salesforce: emptyCACService('Salesforce'),
  slack: emptyCACService('Slack'),
  webex: emptyCACService('Cisco WebEx'),
}

/**
 * Returns true when a CAC allowed URL pattern is listed under
 * {@link CAC_ALLOWED_DOMAINS_ERROR} for the selected cloud app.
 */
export const isCacDomainInAllowedDomainsErrorList = (
  cacDomainPattern: string,
  selectedCloudApp: string,
): boolean => {
  const service =
    CAC_ALLOWED_DOMAINS_ERROR[
      selectedCloudApp.toLowerCase() as keyof CACServices
    ]
  if (!service?.allowedUrls?.length) {
    return false
  }
  return service.allowedUrls.includes(cacDomainPattern)
}

/**
 * Merges CAC data from API response with the existing CAC allowed domains constant.
 * @param apiCACData - The CAC data received from the API
 * @returns Merged CAC services data with updated enabled status and custom domains
 */
export const mergeCACDataFromAPIResponse = (
  apiCACData: CloudAppListTypes,
): CACServices => {
  const mergedCACData: CACServices = {...CAC_ALLOWED_DOMAINS}

  for (const [serviceKey, serviceData] of Object.entries(apiCACData)) {
    const serviceName = serviceKey.toLowerCase() as keyof CACServices
    if (mergedCACData[serviceName]) {
      mergedCACData[serviceName].isEnabled = serviceData.is_enabled
      mergedCACData[serviceName].customDomains =
        getCacConfiguredValues(serviceData)
    }
  }

  return mergedCACData
}

/**
 * Check if a domain exactly matches any CAC managed domain.
 * Wildcard entries are not expanded; for example, `*.gmail.com` only matches
 * `*.gmail.com`, not `gmail.com` or `aidan.gmail.com`.
 * @param domain - The domain to check
 * @returns Object containing match status and matched domain pattern
 */
export const checkDomainAgainstCAC = (
  domain: string,
  cacAllowedDomains: CACServices,
): {isMatch: boolean; matchedDomain?: string; serviceName?: string} => {
  const normalizedDomain = domain.toLowerCase().trim()

  for (const [, service] of Object.entries(cacAllowedDomains)) {
    if (typeof service === 'object' && service.isEnabled) {
      const domainsToCheck = [...service.allowedUrls, ...service.customDomains]
      for (const allowedDomain of domainsToCheck) {
        if (normalizedDomain === allowedDomain.toLowerCase().trim()) {
          return {
            isMatch: true,
            matchedDomain: allowedDomain,
            serviceName: service.displayName,
          }
        }
      }
    }
  }

  return {isMatch: false}
}
